import { getAvailableLocales, useCheckoutTracking, useTranslation } from '@checkout/core'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import styles from './LanguageSwitcher.module.css'

import type { Locale } from '@checkout/core'

export interface LanguageSwitcherProps {
  /** Custom class name */
  className?: string
  /** Size variant */
  size?: 'sm' | 'md'
  /** Show full language name or just code */
  showFullName?: boolean
  /** Callback when language changes */
  onChange?: (locale: Locale) => void
}

export const LanguageSwitcher = forwardRef<HTMLDivElement, LanguageSwitcherProps>(
  ({ className, size = 'md', showFullName = false, onChange }, ref) => {
    const { i18n } = useTranslation()
    const { trackEvent } = useCheckoutTracking()
    const locale = i18n.language as Locale
    const [isOpen, setIsOpen] = useState(false)
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 })
    const menuRef = useRef<HTMLUListElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)

    const locales = getAvailableLocales()
    const currentLocale = locales.find((l) => l.code === locale)

    // Calculate menu position based on trigger button
    const updateMenuPosition = useCallback(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        setMenuPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
        })
      }
    }, [])

    const handleSelect = useCallback(
      (newLocale: Locale) => {
        void i18n.changeLanguage(newLocale)
        trackEvent('language_change', 'checkout_header', { language: newLocale })
        setIsOpen(false)
        onChange?.(newLocale)
      },
      [i18n, onChange, trackEvent]
    )

    const handleToggle = useCallback(() => {
      if (!isOpen) {
        updateMenuPosition()
      }
      setIsOpen((prev) => !prev)
    }, [isOpen, updateMenuPosition])

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node
        // Check if click is outside both trigger and menu
        if (
          menuRef.current &&
          !menuRef.current.contains(target) &&
          triggerRef.current &&
          !triggerRef.current.contains(target)
        ) {
          setIsOpen(false)
        }
      }

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside)
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [isOpen])

    // Close on escape key
    useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsOpen(false)
        }
      }

      if (isOpen) {
        document.addEventListener('keydown', handleEscape)
      }

      return () => {
        document.removeEventListener('keydown', handleEscape)
      }
    }, [isOpen])

    // Update position on scroll/resize when open
    useEffect(() => {
      if (!isOpen) return

      const handleUpdate = () => updateMenuPosition()
      window.addEventListener('scroll', handleUpdate, true)
      window.addEventListener('resize', handleUpdate)
      return () => {
        window.removeEventListener('scroll', handleUpdate, true)
        window.removeEventListener('resize', handleUpdate)
      }
    }, [isOpen, updateMenuPosition])

    // Render menu in portal for mobile to avoid overflow clipping
    const menuContent = isOpen && (
      <ul
        ref={menuRef}
        className={styles.menu}
        role="listbox"
        aria-label="Select language"
        style={{
          position: 'fixed',
          top: menuPosition.top,
          right: menuPosition.right,
        }}
      >
        {locales.map((item) => (
          <li key={item.code} role="option" aria-selected={item.code === locale}>
            <button
              type="button"
              className={`${styles.menuItem} ${item.code === locale ? styles.menuItemActive : ''}`}
              onClick={() => handleSelect(item.code)}
            >
              <span className={styles.menuItemName}>{item.name}</span>
              {item.code === locale && <CheckIcon className={styles.checkIcon} />}
            </button>
          </li>
        ))}
      </ul>
    )

    return (
      <div ref={ref} className={`${styles.wrapper} ${className ?? ''}`} data-size={size}>
        <div className={styles.dropdown}>
          <button
            ref={triggerRef}
            type="button"
            className={styles.trigger}
            onClick={handleToggle}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            data-ga-action-name="language_selector_open"
            data-ga-module="checkout_header"
          >
            <GlobeIcon className={styles.icon} />
            <span className={styles.label}>
              {showFullName ? currentLocale?.name : locale.toUpperCase()}
            </span>
            <ChevronDownIcon className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} />
          </button>

          {/* Render menu in portal to body to avoid overflow clipping */}
          {menuContent && createPortal(menuContent, document.body)}
        </div>
      </div>
    )
  }
)

LanguageSwitcher.displayName = 'LanguageSwitcher'

// Icons
function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  )
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}
